import React, {useCallback, useMemo} from 'react';
import {Banner, Label, LoadingSpinner, Text, Toggle} from '@albert-io/atomic';
import {useShallow} from 'zustand/react/shallow';

import {useQuestionTranslationStore} from './TranslationStore';

import './translation-toggle.scss';

interface TranslationHeaderProps {
  showTranslations: boolean;
  onToggle: () => void;
  translationCoverage?: 'none' | 'full' | 'partial';
  fetchingTranslations?: boolean; // TODO: implment loading symbol or something
}

const TranslationHeader = ({
  showTranslations,
  onToggle,
  translationCoverage = 'none',
  fetchingTranslations
}: TranslationHeaderProps) => {
  const renderBannerRow = useCallback(() => {
    if (fetchingTranslations) {
      return <LoadingSpinner bottomMarginLevel={0} className='u-mar-lr_auto' />;
    }
    const noTranslation = translationCoverage === 'none';
    const hasSubText = translationCoverage === 'partial';
    const mainText = translationCoverage === 'none' ? translationText.none : translationText.es;
    const mainTextColor = translationCoverage === 'none' ? 'tertiary' : 'info';

    return (
      <>
        <Banner.Icon icon={['fal', 'language']} />
        <Text size='s' color={showTranslations ? mainTextColor : undefined}>
          {mainText}
        </Text>
        {hasSubText && (
          <Text
            size='s'
            className='u-mar-l_auto '
            italic
            color={showTranslations ? 'inherit' : 'tertiary'}
          >
            {translationText.partial}
          </Text>
        )}
        <Toggle
          size='xs'
          disabled={noTranslation}
          checked={showTranslations}
          className={hasSubText ? togglePadding.subtext : togglePadding.noSubtext}
          onChange={onToggle}
        />
      </>
    );
  }, [fetchingTranslations, showTranslations, onToggle, translationCoverage]);

  return (
    <Label tabIndex={0}>
      <Banner
        type={showTranslations ? 'info' : 'neutral'}
        className='translation-toggle'
        cardClassName='translation-toggle__card'
        border={false}
      >
        {renderBannerRow()}
      </Banner>
    </Label>
  );
};

const translationText = {
  none: 'Translations not available for this section',
  es: 'Translate to Spanish',
  partial: 'Partially available'
};

const togglePadding = {
  subtext: 'u-mar-l_1',
  noSubtext: 'u-mar-l_auto'
};

type TranslationWrapperProps = Omit<TranslationHeaderProps, 'translationCoverage'>;

const TranslationHeaderWrapper = (props: TranslationWrapperProps) => {
  const {
    fetchingTranslations,
    translationCoverage,
    isReady,
    hasTranslationAccess,
    showTranslations
  } = useQuestionTranslationStore(
    useShallow((state) => ({
      fetchingTranslations: state.fetchingTranslations,
      translationCoverage: state.translationCoverage,
      isReady: state.isReady,
      hasTranslationAccess: state.hasTranslationAccess,
      showTranslations: state.showTranslations
    }))
  );

  const showTranslationToggle = useMemo(() => {
    if (isReady && hasTranslationAccess) return true;
    return false;
  }, [isReady, hasTranslationAccess]);

  if (!showTranslationToggle) return null;

  return (
    <>
      <TranslationHeader
        showTranslations={props.showTranslations}
        onToggle={props.onToggle}
        translationCoverage={translationCoverage}
        fetchingTranslations={fetchingTranslations}
      />
      {showTranslations && (
        <Banner
          className='translation-toggle--warning'
          border={false}
          type='warning'
          cardClassName='translation-toggle__card--warning'
        >
          <Banner.Icon className='u-mar-l_auto' icon={['fal', 'hand']} />
          <Text color='inherit' size='s' className='u-mar-r_auto'>
            {warningText}
          </Text>
        </Banner>
      )}
    </>
  );
};

const warningText = 'La respuesta se calificará según el texto en inglés; revisa tus respuestas en inglés.';

export default TranslationHeaderWrapper;
